import _ from 'lodash';
import { MessageDescriptor } from 'react-intl';
import { CoreAcquisitionConstants } from 'app/interfaces/Image';
import { MAX_KV, MIN_KV, MIN_MA } from 'app/utils/acquisitionConstants';

const MIN_SECONDS = 0.003;
const MAX_SECONDS = 2;

const GENERATOR_ABAQUES = {
  HF200: {
    maxMA: 200,
    powerLimits: [
      [40, 80, 100, 92, 90, 84, 84, 0, 0, 0, 0, 0], //40kV
      [40, 80, 75, 70, 70, 70, 66, 60, 0, 0, 0, 0], //50
      [40, 70, 69, 68, 65, 63, 59, 60, 0, 0, 0, 0], //60
      [35, 60, 58, 60, 60, 55, 0, 0, 0, 0, 0, 0], //70
      [35, 50, 46, 50, 47, 45, 0, 0, 0, 0, 0, 0], //80
      [35, 40, 40, 40, 42, 0, 0, 0, 0, 0, 0, 0], //90
      [30, 30, 32, 30, 0, 0, 0, 0, 0, 0, 0, 0], //100
      [25, 30, 32, 30, 0, 0, 0, 0, 0, 0, 0, 0], //110
      [15, 24, 28, 28, 0, 0, 0, 0, 0, 0, 0, 0], //120
    ] as number[][],
  },
  HF250: {
    maxMA: 300,
    powerLimits: [
      [40, 80, 100, 92, 90, 84, 84, 0, 0, 0, 0, 0], //40kV
      [40, 80, 75, 70, 70, 70, 66, 60, 58, 55, 55, 54], //41 à 50kV            //valable en theorie que pour 50kV
      [40, 70, 69, 68, 65, 63, 59, 60, 58, 55, 55, 54], // 51 à 60kV
      [35, 60, 58, 60, 60, 55, 52, 52, 49, 50, 44, 0], // 61 à 70kV
      [35, 50, 46, 50, 47, 45, 45, 44, 40, 40, 0, 0], // 71 à 80kV
      [35, 40, 40, 40, 42, 39, 38, 36, 0, 0, 0, 0], // 81 à 90kV
      [30, 30, 32, 30, 30, 31, 31, 32, 0, 0, 0, 0], // 91 à 100kV
      [25, 30, 32, 30, 30, 30, 28, 0, 0, 0, 0, 0], // 101 à 110kV
      [15, 24, 28, 28, 25, 24, 0, 0, 0, 0, 0, 0], // 111 à 120kV
    ] as number[][],
  },
  HF300: {
    maxMA: 300,
    powerLimits: [
      [40, 80, 100, 92, 90, 84, 84, 0, 0, 0, 0, 0], //40kV
      [40, 80, 75, 70, 70, 70, 66, 60, 58, 55, 55, 54], //50
      [40, 70, 69, 68, 65, 63, 59, 60, 58, 55, 55, 54], //60
      [35, 60, 58, 60, 60, 55, 52, 52, 49, 50, 44, 42], //70
      [35, 50, 46, 50, 47, 45, 45, 44, 40, 40, 38, 36], //80
      [35, 40, 40, 40, 42, 39, 38, 36, 36, 35, 33, 30], //90
      [30, 30, 32, 30, 30, 31, 31, 32, 31, 30, 27, 12], //100
      [25, 30, 32, 30, 30, 30, 28, 28, 27, 25, 0, 0], //110
      [15, 24, 28, 28, 25, 24, 21, 20, 18, 5, 0, 0], //120
    ] as number[][],
  },
} as const;

function adjustConstantsToPowerLimits(
  { mA, s }: { mA: number; s: number },
  mAsLimits: number[]
): { mA: number; s: number } {
  let adjustedMA = mA;
  const mAIndex = Math.floor(mA / 25) - 1;
  let adjustedS = s;
  let requestedMAS = mA * s;
  console.debug('Requested mAs:', requestedMAS);
  // First we check if mAs is possible.
  const maxMAS = _.max(mAsLimits);
  console.debug('maxMAS:', maxMAS);
  if (requestedMAS > maxMAS) {
    // mAs constraint is violated, we set to max possible
    requestedMAS = maxMAS;
  }

  for (let i = mAIndex; i >= 0; i--) {
    if (mAsLimits[i] < requestedMAS) continue;
    adjustedMA = (i + 1) * 25;
    adjustedS = _.round(requestedMAS / adjustedMA, 3);
    break;
  }
  return { mA: adjustedMA, s: adjustedS };
}

export default function computeClosestCompatibleGeneratorConstants(
  generatorModel: string,
  constants: CoreAcquisitionConstants
) {
  const initialConstants = { ...constants };
  let { kV, mA, s } = constants;

  const kvAbaque = GENERATOR_ABAQUES[generatorModel as keyof typeof GENERATOR_ABAQUES];

  if (kV < MIN_KV) kV = MIN_KV;
  if (kV > MAX_KV) kV = MAX_KV;
  if (s < MIN_SECONDS) s = MIN_SECONDS;
  if (s > MAX_SECONDS) s = MAX_SECONDS;

  const powerLimits = kvAbaque.powerLimits[Math.floor((kV - 31) / 10)];

  const adjustedConstants = adjustConstantsToPowerLimits({ mA, s }, powerLimits);

  mA = adjustedConstants.mA;
  s = adjustedConstants.s;

  return {
    kV,
    mA,
    s,
  };
}
